/**
 * affiliate-tax-page.tsx — Platform → Tiếp thị liên kết → Thuế TNCN. Route `/platform/affiliate/tax`.
 *
 * Báo cáo khấu trừ thuế TNCN theo kỳ (tháng / quý / năm) để kê khai với cơ quan thuế (E14 — quyết định 29/09/2026):
 * mỗi người nhận — tên, CCCD/MST, loại, tổng thu nhập đã chi (gộp), thuế đã khấu trừ, số lần chi — kèm tổng cộng.
 * Nguồn: lệnh rút ĐÃ CHI theo ngày chi. Màn hình che CCCD; file CSV kê khai có số đầy đủ (ghi nhật ký Platform).
 * Quy tắc khấu trừ giữ theo từng lần chi (< 2.000.000đ không khấu trừ; ≥ 2.000.000đ khấu trừ 10% — theo cấu hình).
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { Download, Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { formatVnd } from '@/lib/format-money'
import { platformApiError } from '@/lib/platform-client'
import { defaultPeriod, periodParams, type PeriodSel } from '@/lib/affiliate'
import { downloadTaxReportCsv, useTaxReport, type TaxReportRow } from '@/hooks/use-platform-affiliate'
import { PeriodPicker } from '@/pages/affiliate/tax-certificate'

export function AffiliateTaxPage() {
  const [p, setP] = useState<PeriodSel>(() => ({ ...defaultPeriod(), kind: 'month' }))
  const params = periodParams(p)
  const { data, isLoading } = useTaxReport(params)
  const [busy, setBusy] = useState(false)

  const exportCsv = async () => {
    setBusy(true)
    try {
      await downloadTaxReportCsv(params, data?.period.slug ?? 'ky')
    } catch (e) {
      toast.error(platformApiError(e))
    } finally {
      setBusy(false)
    }
  }

  const columns: Column<TaxReportRow>[] = [
    { key: 'name', header: 'Người nhận', cell: (r) => <div><p className="font-medium">{r.recipientName}</p><p className="font-mono text-xs text-muted-foreground">{r.partnerCode}</p></div> },
    { key: 'type', header: 'Loại', cell: (r) => (r.partnerType === 'company' ? 'Doanh nghiệp' : 'Cá nhân') },
    { key: 'id', header: 'CCCD / MST', cell: (r) => r.idOrTaxCode || '—' },
    { key: 'n', header: 'Số lần chi', align: 'right', cell: (r) => `${r.payouts}${r.withheldPayouts ? ` (${r.withheldPayouts} khấu trừ)` : ''}` },
    { key: 'gross', header: 'Thu nhập đã chi (gộp)', align: 'right', cell: (r) => formatVnd(r.gross) },
    { key: 'tax', header: 'Thuế TNCN đã khấu trừ', align: 'right', cell: (r) => <span className="font-medium">{formatVnd(r.taxWithheld)}</span> },
    { key: 'net', header: 'Thực chi', align: 'right', cell: (r) => formatVnd(r.net) },
  ]

  return (
    <div className="space-y-5">
      <PageHeader
        title="Thuế TNCN hoa hồng"
        description="Báo cáo khấu trừ thuế TNCN theo kỳ để kê khai: chỉ gồm lệnh rút đã chi, tính theo ngày chi (giờ Việt Nam)."
        actions={
          <Button onClick={exportCsv} disabled={busy || !data?.rows.length}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Xuất CSV kê khai
          </Button>
        }
      />
      <PeriodPicker value={p} onChange={setP} />
      {data && (
        <div className="grid gap-3 sm:grid-cols-4">
          <Card className="p-4"><p className="text-xs text-muted-foreground">Người nhận</p><p className="text-lg font-semibold">{data.totals.recipients}</p></Card>
          <Card className="p-4"><p className="text-xs text-muted-foreground">Tổng thu nhập đã chi</p><p className="text-lg font-semibold">{formatVnd(data.totals.gross)}</p></Card>
          <Card className="p-4"><p className="text-xs text-muted-foreground">Tổng thuế đã khấu trừ</p><p className="text-lg font-semibold">{formatVnd(data.totals.taxWithheld)}</p></Card>
          <Card className="p-4"><p className="text-xs text-muted-foreground">Số lần chi</p><p className="text-lg font-semibold">{data.totals.payouts}</p></Card>
        </div>
      )}
      <DataTable columns={columns} rows={data?.rows ?? []} loading={isLoading} rowKey={(r) => r.partnerId} emptyTitle={`Không có khoản chi nào trong ${data?.period.label ?? 'kỳ'}`} />
      <p className="text-xs text-muted-foreground">
        CCCD được che trên màn hình; file CSV (UTF-8, mở bằng Excel) có số đầy đủ để kê khai — tải file được ghi nhật ký. Quy tắc khấu trừ: theo từng lần chi
        (dưới ngưỡng không khấu trừ; từ ngưỡng trở lên khấu trừ theo thuế suất cấu hình; cam kết 08 / doanh nghiệp không khấu trừ). Số liệu phải được kế
        toán đối chiếu trước khi nộp. Chứng từ khấu trừ cho từng đối tác: mở chi tiết đối tác → "Chứng từ khấu trừ thuế TNCN".
      </p>
    </div>
  )
}
